import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import type { CloudService, CloudProvider, CloudEnvironment, Criticality } from '@/types/cloudService';
import { UserSelector } from '@/components/shared/UserSelector';

interface CloudServiceFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: any) => Promise<void>;
  cloudService?: CloudService;
}

const CLOUD_PROVIDERS: CloudProvider[] = ['AWS', 'Azure', 'GCP', 'Oracle', 'IBM', 'Other'];
const CLOUD_ENVIRONMENTS: CloudEnvironment[] = ['Production', 'Staging', 'Development', 'Testing'];
const CRITICALITY_LEVELS: Criticality[] = ['Low', 'Medium', 'High'];

const AWS_REGIONS = [
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-east-2', label: 'US East (Ohio)' },
  { value: 'us-west-1', label: 'US West (N. California)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'eu-north-1', label: 'Europe (Stockholm)' },
  { value: 'eu-west-1', label: 'Europe (Ireland)' },
  { value: 'eu-central-1', label: 'Europe (Frankfurt)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
];

const AZURE_REGIONS = [
  { value: 'eastus', label: 'East US' },
  { value: 'westeurope', label: 'West Europe' },
  { value: 'northeurope', label: 'North Europe' },
  { value: 'southeastasia', label: 'Southeast Asia' },
];

const GCP_REGIONS = [
  { value: 'us-central1', label: 'Iowa (us-central1)' },
  { value: 'europe-west1', label: 'Belgium (europe-west1)' },
  { value: 'europe-north1', label: 'Finland (europe-north1)' },
  { value: 'asia-southeast1', label: 'Singapore (asia-southeast1)' },
];

export function CloudServiceFormDialog({ open, onOpenChange, onSubmit, cloudService }: CloudServiceFormDialogProps) {
  const [name, setName] = useState(cloudService?.name || '');
  const [provider, setProvider] = useState<CloudProvider>(cloudService?.provider || 'AWS');
  const [accountId, setAccountId] = useState(cloudService?.account_id || '');
  const [subscriptionId, setSubscriptionId] = useState(cloudService?.subscription_id || '');
  const [projectId, setProjectId] = useState(cloudService?.project_id || '');
  const [region, setRegion] = useState(cloudService?.region || '');
  const [environment, setEnvironment] = useState<CloudEnvironment | undefined>(cloudService?.environment);
  const [criticality, setCriticality] = useState<Criticality | undefined>(cloudService?.criticality);
  const [tags, setTags] = useState<string[]>(cloudService?.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [notes, setNotes] = useState(cloudService?.notes || '');
  const [ownerId, setOwnerId] = useState<string | undefined>(cloudService?.owner_id);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (cloudService) {
      setName(cloudService.name);
      setProvider(cloudService.provider);
      setAccountId(cloudService.account_id || '');
      setSubscriptionId(cloudService.subscription_id || '');
      setProjectId(cloudService.project_id || '');
      setRegion(cloudService.region || '');
      setEnvironment(cloudService.environment);
      setCriticality(cloudService.criticality);
      setTags(cloudService.tags || []);
      setNotes(cloudService.notes || '');
      setOwnerId(cloudService.owner_id);
    }
  }, [cloudService]);

  const getRegionsForProvider = () => {
    switch (provider) {
      case 'AWS': return AWS_REGIONS;
      case 'Azure': return AZURE_REGIONS;
      case 'GCP': return GCP_REGIONS;
      default: return [];
    }
  };

  const getProviderIdLabel = () => {
    switch (provider) {
      case 'AWS': return 'Account ID';
      case 'Azure': return 'Subscription ID';
      case 'GCP': return 'Project ID';
      default: return 'Service ID';
    }
  };

  const getProviderIdValue = () => {
    switch (provider) {
      case 'AWS': return accountId;
      case 'Azure': return subscriptionId;
      case 'GCP': return projectId;
      default: return accountId;
    }
  };

  const setProviderIdValue = (value: string) => {
    switch (provider) {
      case 'AWS': setAccountId(value); break;
      case 'Azure': setSubscriptionId(value); break;
      case 'GCP': setProjectId(value); break;
      default: setAccountId(value); break;
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter(t => t !== tag));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data: any = {
        name,
        provider,
        region: region || undefined,
        environment: environment || undefined,
        criticality: criticality || undefined,
        tags: tags.length > 0 ? tags : undefined,
        notes: notes || undefined,
        owner_id: ownerId,
      };

      // Add provider-specific ID
      if (provider === 'AWS') data.account_id = accountId || undefined;
      if (provider === 'Azure') data.subscription_id = subscriptionId || undefined;
      if (provider === 'GCP') data.project_id = projectId || undefined;

      await onSubmit(data);
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{cloudService ? 'Edit Cloud Service' : 'Create Cloud Service'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Service Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="e.g., Production AWS Account"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="provider">Provider *</Label>
              <Select value={provider} onValueChange={(v) => setProvider(v as CloudProvider)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CLOUD_PROVIDERS.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="provider-id">{getProviderIdLabel()}</Label>
              <Input
                id="provider-id"
                value={getProviderIdValue()}
                onChange={(e) => setProviderIdValue(e.target.value)}
                placeholder={`Enter ${getProviderIdLabel()}`}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {getRegionsForProvider().length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="region">Region</Label>
                <Select value={region} onValueChange={setRegion}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select region" />
                  </SelectTrigger>
                  <SelectContent>
                    {getRegionsForProvider().map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="environment">Environment</Label>
              <Select value={environment} onValueChange={(v) => setEnvironment(v as CloudEnvironment)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select environment" />
                </SelectTrigger>
                <SelectContent>
                  {CLOUD_ENVIRONMENTS.map(e => (
                    <SelectItem key={e} value={e}>{e}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="criticality">Criticality</Label>
            <Select value={criticality} onValueChange={(v) => setCriticality(v as Criticality)}>
              <SelectTrigger>
                <SelectValue placeholder="Select criticality" />
              </SelectTrigger>
              <SelectContent>
                {CRITICALITY_LEVELS.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <UserSelector
            value={ownerId}
            onChange={setOwnerId}
            label="Owner"
            placeholder="Select owner"
          />

          <div className="space-y-2">
            <Label>Tags</Label>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                placeholder="Add tag and press Enter"
              />
              <Button type="button" onClick={handleAddTag} size="sm">
                Add
              </Button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map(tag => (
                  <Badge key={tag} variant="secondary" className="gap-1">
                    {tag}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => handleRemoveTag(tag)} />
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes about this cloud service"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !name}>
              {loading ? 'Saving...' : cloudService ? 'Update Service' : 'Create Service'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
